import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { getGatewayBaseUrl } from "../runtime/config";
import {
  PosReceivingGatewayClient,
  type ReceivingEvent,
  type ReceivingQueue,
  type ReceivingQueueReceipt,
  type ReceivingVoidReason,
} from "../runtime/gateway/receiving-client";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import "./ReceivingPanel.css";
import "./ReceivingVoidRuntimeHost.css";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  employeeSession: LocalEmployeeSessionState | null;
};

type VoidSelection = {
  receipt: ReceivingQueueReceipt;
  event: ReceivingEvent;
  stage: "INPUT" | "REVIEW";
  requestKey: string;
};

function requestKey() {
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-purchase-receiving-void:${id}`;
}

function formatDateTime(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

function quantityText(value: number, unit: string | null) {
  const text = Number(value).toLocaleString("th-TH", { maximumFractionDigits: 3 });
  return unit ? `${text} ${unit}` : text;
}

function displayError(value: string) {
  if (value.includes("GATEWAY_UNREACHABLE")) return "ติดต่อ MORE POS Gateway ไม่ได้";
  if (value.includes("GATEWAY_TIMEOUT")) return "Gateway ตอบกลับช้าเกินไป กรุณาลองอีกครั้งด้วยคำขอเดิม";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "Session พนักงานใช้ไม่ได้แล้ว กรุณา Login ใหม่";
  if (value.includes("POS_STOCK_RECEIVING_PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์จัดการรับสินค้า";
  if (value.includes("PURCHASE_RECEIVING_STATE_CHANGED")) return "สถานะการรับสินค้าเปลี่ยนแล้ว กรุณารีเฟรช";
  if (value.includes("PURCHASE_RECEIVING_VOID_NOTE_REQUIRED")) return "กรุณาระบุรายละเอียดประกอบการยกเลิก";
  if (value.includes("PURCHASE_RECEIVING_VOID_REASON_INVALID")) return "กรุณาเลือกเหตุผลที่ยกเลิกการรับสินค้า";
  if (value.includes("PURCHASE_RECEIVING_REQUEST_KEY_CONFLICT")) return "คำขอนี้ขัดกับข้อมูลเดิม กรุณารีเฟรช";
  return "ยกเลิกรายการรับสินค้าไม่สำเร็จ กรุณารีเฟรชแล้วลองอีกครั้ง";
}

function ReceivingVoidPanel({ terminal, session, online }: {
  terminal: TerminalCredentialState;
  session: LocalEmployeeSessionState;
  online: boolean;
}) {
  const actor = session.actor;
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState<ReceivingQueue | null>(null);
  const [reasons, setReasons] = useState<ReceivingVoidReason[]>([]);
  const [selection, setSelection] = useState<VoidSelection | null>(null);
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const withClient = useCallback(async <T,>(run: (
    client: PosReceivingGatewayClient,
    auth: { deviceSecret: string; sessionToken: string },
  ) => Promise<T>): Promise<T> => {
    const baseUrl = getGatewayBaseUrl();
    const currentSession = await readLocalEmployeeSession();
    if (!baseUrl || !terminal.deviceSecret || !currentSession?.sessionToken) throw new Error("POS_RECEIVING_RUNTIME_NOT_READY");
    if (currentSession.actor.runtime_session_id !== actor.runtime_session_id) throw new Error("POS_EMPLOYEE_RUNTIME_SESSION_INVALID");
    return run(new PosReceivingGatewayClient({ baseUrl }), {
      deviceSecret: terminal.deviceSecret,
      sessionToken: currentSession.sessionToken,
    });
  }, [actor.runtime_session_id, terminal.deviceSecret]);

  const load = useCallback(async () => {
    if (!online) return;
    setLoading(true);
    setError(null);
    try {
      const result = await withClient((client, auth) => client.queue({ ...auth, includeResolved: true }));
      if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
      setQueue(result.data);
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    } finally {
      setLoading(false);
    }
  }, [online, withClient]);

  useEffect(() => {
    if (online) void load();
  }, [load, online]);

  const receiptsWithEvents = useMemo(
    () => (queue?.receipts ?? []).filter((receipt) => (receipt.receiving_events?.length ?? 0) > 0),
    [queue],
  );

  const voidableCount = useMemo(
    () => receiptsWithEvents.reduce((sum, receipt) => sum + (receipt.receiving_events ?? []).filter((event) => event.event_status === "POSTED").length, 0),
    [receiptsWithEvents],
  );

  async function chooseVoid(receipt: ReceivingQueueReceipt, event: ReceivingEvent) {
    setSelection({ receipt, event, stage: "INPUT", requestKey: requestKey() });
    setReasonCode("");
    setNote("");
    setError(null);
    if (reasons.length || !online) return;
    try {
      const result = await withClient((client, auth) => client.voidReasons(auth));
      if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
      setReasons(result.data.reasons ?? []);
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    }
  }

  function reviewVoid() {
    if (!selection) return;
    const reason = reasons.find((item) => item.reason_code === reasonCode);
    if (!reason) {
      setError("กรุณาเลือกเหตุผลที่ยกเลิกการรับสินค้า");
      return;
    }
    if (reason.requires_note && !note.trim()) {
      setError("กรุณาระบุรายละเอียดประกอบการยกเลิก");
      return;
    }
    setError(null);
    setSelection({ ...selection, stage: "REVIEW" });
  }

  async function confirmVoid() {
    if (!selection || !online || !reasonCode) return;
    setSaving(true);
    setError(null);
    try {
      const result = await withClient((client, auth) => client.voidReceive({
        ...auth,
        receivingEventId: selection.event.event_id,
        reasonCode,
        requestKey: selection.requestKey,
        note: note.trim(),
      }));
      if (!result.ok || !result.data) throw new Error(result.error ?? "POS_STOCK_RECEIVING_FAILED");
      setSuccess("ยกเลิกการรับแล้ว Stock Core สร้าง Reversal และเปิดยอดค้างรับกลับตามจริง");
      setSelection(null);
      setReasonCode("");
      setNote("");
      await load();
    } catch (cause) {
      setError(displayError(cause instanceof Error ? cause.message : String(cause)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button
        className="receiving-launch receiving-launch--history"
        type="button"
        onClick={() => { setOpen(true); setSuccess(null); if (online) void load(); }}
      >
        <span aria-hidden="true">↶</span>
        ประวัติรับ
        {voidableCount > 0 ? <b>{voidableCount}</b> : null}
      </button>

      {open ? (
        <div className="receiving-backdrop" role="presentation">
          <section className="receiving-panel" role="dialog" aria-modal="true" aria-labelledby="receiving-void-title">
            <header className="receiving-panel__header">
              <div>
                <p>MORE STOCK · RECEIVING HISTORY</p>
                <h2 id="receiving-void-title">ประวัติการรับ / ยกเลิก</h2>
                <span>{actor.branch_name} · {actor.employee_name}</span>
              </div>
              <button type="button" disabled={saving} aria-label="ปิดประวัติรับสินค้า" onClick={() => { setOpen(false); setSelection(null); }}>×</button>
            </header>

            {!online ? <div className="receiving-alert">Offline: ดูหน้าขายต่อได้ แต่การอ่านประวัติและยกเลิกรับต้อง Online เพราะ Stock Core เป็น Final Authority</div> : null}
            {success ? <div className="receiving-success">{success}</div> : null}
            {error ? <div className="receiving-error">{error}</div> : null}

            <div className="receiving-content">
              {!selection ? (
                <>
                  <div className="receiving-history-toolbar">
                    <div><strong>{voidableCount}</strong><span>การรับที่ยังยกเลิกได้</span></div>
                    <button type="button" disabled={!online || loading} onClick={() => void load()}>{loading ? "กำลังอ่าน…" : "รีเฟรช"}</button>
                  </div>

                  {!receiptsWithEvents.length ? (
                    <div className="receiving-empty"><strong>ยังไม่มีประวัติการรับสินค้า</strong><p>เมื่อยืนยันรับจริง รายการจะปรากฏที่นี่</p></div>
                  ) : (
                    <div className="receiving-list">
                      {receiptsWithEvents.map((receipt) => (
                        <article className="receiving-receipt" key={receipt.receipt_id}>
                          <div className="receiving-receipt__head">
                            <div><h3>{receipt.supplier_name || "ไม่ระบุผู้ขาย"}</h3><p>{receipt.internal_bill_no || receipt.document_ref || receipt.receipt_id}</p></div>
                            <div className="receiving-receipt__numbers"><span>รับสุทธิ</span><strong>{Number(receipt.received_stock_qty)}</strong><small>หน่วยรวม</small></div>
                          </div>
                          <div className="receiving-history-events">
                            {(receipt.receiving_events ?? []).map((event) => (
                              <div className="receiving-history-event" key={event.event_id}>
                                <div className="receiving-history-event__head">
                                  <div>
                                    <span className={`receiving-state ${event.event_status === "VOIDED" ? "receiving-state--voided" : "receiving-state--partial"}`}>
                                      {event.event_status === "POSTED" ? "รับเข้าสต็อกแล้ว" : "ยกเลิกแล้ว"}
                                    </span>
                                    <small>{formatDateTime(event.occurred_at)}</small>
                                  </div>
                                  {event.event_status === "POSTED" ? <button type="button" disabled={!online} onClick={() => void chooseVoid(receipt, event)}>ยกเลิกการรับครั้งนี้</button> : null}
                                </div>
                                <div className="receiving-history-lines">
                                  {event.lines.map((line) => <span key={`${event.event_id}:${line.purchase_line_id}`}><b>{line.sku_name || line.sku_code || "สินค้า"}</b> +{quantityText(Number(line.quantity_stock), line.stock_unit_name)}</span>)}
                                </div>
                                {event.event_status === "VOIDED" ? <p className="receiving-history-void-note">ยกเลิก {formatDateTime(event.voided_at)} · {event.void_reason_label || event.void_reason_code}{event.void_note ? ` · ${event.void_note}` : ""}</p> : null}
                              </div>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="receiving-action-view">
                  <button className="receiving-back" type="button" disabled={saving} onClick={() => { setSelection(null); setError(null); }}>← กลับประวัติ</button>
                  <div className="receiving-action-title"><div><small>ยกเลิกการรับสินค้า</small><h3>{selection.receipt.supplier_name || "ไม่ระบุผู้ขาย"}</h3></div><span>{formatDateTime(selection.event.occurred_at)}</span></div>

                  {selection.stage === "INPUT" ? (
                    <>
                      <p className="receiving-void-warning">ใช้เมื่อการรับครั้งนี้ “บันทึกผิด” เท่านั้น ระบบจะ Reversal จำนวนทั้งหมดของครั้งนี้ ประวัติเดิมไม่ถูกลบ และนี่ไม่ใช่การคืนสินค้าให้ผู้ขาย</p>
                      <div className="receiving-edit-lines">
                        {selection.event.lines.map((line) => (
                          <label key={line.purchase_line_id}><span><b>{line.sku_name || line.sku_code || "สินค้า"}</b><small>จำนวนที่รับครั้งนี้</small></span><strong className="receiving-void-qty">−{quantityText(Number(line.quantity_stock), line.stock_unit_name)}</strong></label>
                        ))}
                      </div>
                      <label className="receiving-field"><span>เหตุผล</span><select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}><option value="">เลือกเหตุผล</option>{reasons.map((reason) => <option value={reason.reason_code} key={reason.reason_code}>{reason.label_th}</option>)}</select></label>
                      <label className="receiving-field"><span>รายละเอียด</span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="ระบุสิ่งที่เกิดขึ้นเพื่อให้ตรวจย้อนหลังได้" /></label>
                      <button className="receiving-primary receiving-review" type="button" onClick={reviewVoid}>ตรวจสอบก่อนยืนยัน</button>
                    </>
                  ) : (
                    <div className="receiving-review-box">
                      <p className="receiving-review-warning">ตรวจทานอีกครั้ง: Stock จะลดกลับตรงกับการรับครั้งนี้ทั้งหมด และระบบจะเก็บทั้ง Movement เดิมกับ Reversal</p>
                      {selection.event.lines.map((line) => <div key={line.purchase_line_id}><span>{line.sku_name || line.sku_code || "สินค้า"}</span><strong className="receiving-void-qty">−{quantityText(Number(line.quantity_stock), line.stock_unit_name)}</strong></div>)}
                      <div><span>เหตุผล</span><strong>{reasons.find((item) => item.reason_code === reasonCode)?.label_th || reasonCode}</strong></div>
                      <div><span>รายละเอียด</span><strong>{note.trim()}</strong></div>
                      <div className="receiving-review-actions">
                        <button className="receiving-back" type="button" disabled={saving} onClick={() => setSelection({ ...selection, stage: "INPUT" })}>กลับไปแก้ไข</button>
                        <button className="receiving-confirm receiving-confirm--danger" type="button" disabled={saving || !online} onClick={() => void confirmVoid()}>{saving ? "กำลังยกเลิก…" : "ยืนยันยกเลิกการรับ"}</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

export function ReceivingVoidRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ terminal: null, employeeSession: null });
  const [online, setOnline] = useState(navigator.onLine);
  const [mountNode, setMountNode] = useState<Element | null>(null);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      employeeSession: await readLocalEmployeeSession(),
    })).subscribe({ next: setRuntime, error: () => setRuntime({ terminal: null, employeeSession: null }) });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateNetwork = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateNetwork);
    window.addEventListener("offline", updateNetwork);
    return () => { window.removeEventListener("online", updateNetwork); window.removeEventListener("offline", updateNetwork); };
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-header__actions"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const terminal = runtime.terminal;
  const session = runtime.employeeSession;
  const actor = session?.actor ?? null;
  const identity = terminal?.identity ?? null;
  const sameScope = Boolean(actor && identity
    && actor.organization_id === identity.organization_id
    && actor.branch_id === identity.branch_id
    && actor.node_id === identity.node_id
    && actor.runtime_session_status === "ACTIVE");
  const canReceive = actor?.permission_codes.includes("stock.receive.purchase") ?? false;

  if (!mountNode || !terminal || !session || !actor || !sameScope || !canReceive) return null;
  return createPortal(<ReceivingVoidPanel terminal={terminal} session={session} online={online} />, mountNode);
}
